import { faPlus } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useFilters } from '@domain-group/react-hooks'

import {
  Box, Button, DashboardModule,
} from '@domain-group/suit-theme'

import useProviderProductCode from '@hooks/useProviderProductCode'
import useProviderProductCodes from '@hooks/useProviderProductCodes'
import useServices from '@hooks/useServices'

import type { ServiceModel } from '@models/types'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import ProviderProductCodeListItem from './ProviderProductCodeListItem'

const buildPageFilterFields = (services: ServiceModel[]) => {
  const style = {
    marginBottom: 16,
  }

  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Product Code',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        endpoint: 'service_products',
        entityKey: 'serviceProducts',
      },
      key: 'service_product_id',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      label: 'Service',
      values: services.map(service => ({
        key: service.id,
        label: service.title,
      })),
    },
  ]
}

const Products = (props: {providerId: number}) => {
  const { providerId } = props

  const filterPayload = useFilters()
  const {
    pageFilters,
  } = filterPayload

  const {
    callbacks: { createOrEditProviderProductCode },
  } = useProviderProductCode({ provider_id: providerId })

  const options = {
    performHttpRequests: !!providerId,
    filters: { ...pageFilters, provider_id: providerId },
  }
  const providerProductCodesPayload = useProviderProductCodes(options)
  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderProductCodes,
    filteredProviderProductCodesCount,
    hasFilteredProviderProductCodes,
    loading,
  } = providerProductCodesPayload

  const { filteredServices } = useServices()

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-products' }}
      contentBoxProps={{ flexDirection: 'column' }}
      title="Products"
      loading={loading}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields(filteredServices)}
        {...filterPayload}
      />

      <Box flexDirection="column">
        {!loading && !hasFilteredProviderProductCodes && (
          <DashboardModule.BlankState>
            <DashboardModule.BlankState.Title>
              No Products present
            </DashboardModule.BlankState.Title>

            <DashboardModule.BlankState.Paragraph>
              Please assign a Product below.
            </DashboardModule.BlankState.Paragraph>
          </DashboardModule.BlankState>
        )}

        {!loading && hasFilteredProviderProductCodes && (
          // @ts-expect-error TS2322
          <DashboardModule.ScrollableSection flexDirection="column" maxHeight={600}>
            {filteredProviderProductCodes.map(providerProductCode => (
              <ProviderProductCodeListItem
                key={providerProductCode.id}
                providerProductCode={providerProductCode}
              />
            ))}
          </DashboardModule.ScrollableSection>
        )}

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredProviderProductCodesCount}
          // @ts-expect-error TS2322
          loading={loading}
        >
          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => createOrEditProviderProductCode()}
            // @ts-expect-error TS2322
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            Add Product
          </Button>
        </DashboardModule.LoadMoreFooter>
      </Box>
    </DashboardModule>
  )
}

export default Products
